// client/src/world/flora/grammar.ts
/**
 * The shared tree grammar: trunk spline → forks → canopy, all species driven
 * by SpeciesParams tables plus a small closed motif set. Same output contract
 * as the legacy builders: ONE merged, non-indexed, flat-shaded, vertex-colored
 * geometry — the wind material and scatter instancing consume it unchanged.
 */
import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { range, type Rng } from "@hyperlight/shared";
import { V, tube, blob, shade, flat, paint } from "./primitives";
import type { BuildCtx, C3, Season, SpeciesParams } from "./types";
import { buildBroadleaf } from "./broadleaf";
import { buildConifer } from "./conifer";
import { buildPalm } from "./palm";
import { buildAcacia } from "./acacia";
import { buildCypress } from "./cypress";
import { TREE_STEM, paintTreeWind, windJoint, type WindJoint } from './treeWind';

export const SNOW: C3 = [0.93, 0.95, 0.98];

function leafColor(sp: SpeciesParams, season: Season): C3 {
  return season === "autumn" ? sp.palette.leafAutumn : sp.palette.leafSummer;
}

/** Pull a color toward its own grey — winter desaturation. */
export function desat(c: C3, k: number): C3 {
  const g = (c[0] + c[1] + c[2]) / 3;
  return [c[0] + (g - c[0]) * k, c[1] + (g - c[1]) * k, c[2] + (g - c[2]) * k];
}

export function buildTree(rng: Rng, sp: SpeciesParams, ctx: BuildCtx): THREE.BufferGeometry {
  if (sp.canopy.style === "cypress") return buildCypress(rng, sp, ctx, false);
  if (sp.canopy.style === "umbrella") return buildAcacia(rng, sp, ctx, false);
  if (sp.canopy.style === "pinnate") return buildPalm(rng, sp, ctx, false);
  if (sp.canopy.style === "blobs") return buildBroadleaf(rng, sp, ctx,
    ctx.season === "winter" ? desat(sp.palette.bark, .3) : sp.palette.bark, leafColor(sp, ctx.season), SNOW, false);
  if (sp.canopy.style === "tiers" || sp.canopy.style === "droopTiers") return buildConifer(rng, sp, ctx,
    ctx.season === "winter" ? desat(sp.palette.bark, .3) : sp.palette.bark,
    ctx.season === "winter" ? desat(leafColor(sp, ctx.season), .35) : leafColor(sp, ctx.season), SNOW, false);
  const parts: THREE.BufferGeometry[] = [];
  // Maturity: young ≈ half-size saplings, old-growth ≈ 1.2× the table reference,
  // with girth and gnarl (lean/spread) growing alongside height.
  const grow = 0.45 + 0.75 * ctx.maturity;
  const girth = 0.65 + 0.6 * ctx.maturity;
  const gnarl = 0.6 + 0.9 * ctx.maturity;
  const h = range(rng, sp.trunk.height[0], sp.trunk.height[1]) * grow;
  const r0 = sp.trunk.radius * girth;
  const winter = ctx.season === "winter";
  const bark: C3 = winter ? desat(sp.palette.bark, 0.3) : sp.palette.bark;
  const leaf: C3 = winter ? desat(leafColor(sp, ctx.season), 0.35) : leafColor(sp, ctx.season);
  const bare = sp.deciduous && winter;

  {
    // Retained willow skeleton: its hanging-frond family has a separate form.
    const lean = sp.trunk.lean * h * gnarl;
    const top = V(range(rng, -1, 1) * lean, h * sp.trunk.forkHeight, range(rng, -1, 1) * lean);
    parts.push(tube(V(0, 0, 0), top, r0, r0 * 0.6, bark));
    addBarkBands(parts, rng, sp, r0, top);
    const forks = Math.round(range(rng, sp.trunk.forks[0], sp.trunk.forks[1]));
    const tips: THREE.Vector3[] = [top.clone()];
    const joints: WindJoint[] = [TREE_STEM];
    for (let b = 0; b < forks; b++) {
      const a = (b / Math.max(1, forks)) * Math.PI * 2 + rng() * 1.2;
      const spread = range(rng, 0.9, 1.8) * gnarl;
      const rise = range(rng, 0.5, 1.4) * (h - top.y) * 0.9 + 0.4;
      const tip = top.clone().add(V(Math.cos(a) * spread, rise, Math.sin(a) * spread));
      const elbow = top.clone().lerp(tip, .5).add(V(0, -.22 * gnarl, 0));
      const joint = windJoint(top, tip, r0 * .5);
      parts.push(paintTreeWind(tube(top, elbow, r0 * .5, r0 * .35, bark), joint));
      parts.push(paintTreeWind(tube(elbow, tip, r0 * .35, r0 * .18, bark), joint));
      tips.push(tip);
      joints.push(joint);
    }
    if (bare) {
      addWinterCrown(parts, rng, tips, r0, bark, joints);
    } else {
      addFronds(parts, rng, sp, tips, leaf, joints);
    }
  }

  // Buttress roots visually seat the trunk; below-ground tips hide on slopes.
  for (let i = 0; i < 4; i++) {
    const a = i * Math.PI / 2 + rng() * .6;
    parts.push(tube(V(0, r0 * 1.8, 0), V(Math.cos(a) * r0 * 2.5, -.08, Math.sin(a) * r0 * 2.5), r0 * .45, r0 * .08, bark, 4));
  }

  for (const part of parts) if (!part.hasAttribute('branchWind')) paintTreeWind(part);
  const merged = mergeGeometries(parts, false);
  if (!merged) throw new Error(`buildTree(${sp.id}): merge failed`);
  for (const p of parts) p.dispose();
  merged.computeVertexNormals();
  return merged;
}

/**
 * FAR-LOD tree: the same species/season/maturity inputs collapsed to a
 * silhouette with a bounded trunk/crown hierarchy (≤150 tris). The far
 * scatter band draws these kilometres out and crossfades to the full builds
 * as the forest band streams in underneath.
 */
export function buildTreeFar(rng: Rng, sp: SpeciesParams, ctx: BuildCtx): THREE.BufferGeometry {
  if (sp.canopy.style === "cypress") return buildCypress(rng, sp, ctx, true);
  if (sp.canopy.style === "umbrella") return buildAcacia(rng, sp, ctx, true);
  if (sp.canopy.style === "pinnate") return buildPalm(rng, sp, ctx, true);
  if (sp.canopy.style === "blobs") return buildBroadleaf(rng, sp, ctx,
    ctx.season === "winter" ? desat(sp.palette.bark, .3) : sp.palette.bark, leafColor(sp, ctx.season), SNOW, true);
  if (sp.canopy.style === "tiers" || sp.canopy.style === "droopTiers") return buildConifer(rng, sp, ctx,
    ctx.season === "winter" ? desat(sp.palette.bark, .3) : sp.palette.bark,
    ctx.season === "winter" ? desat(leafColor(sp, ctx.season), .35) : leafColor(sp, ctx.season), SNOW, true);
  const parts: THREE.BufferGeometry[] = [];
  const grow = 0.45 + 0.75 * ctx.maturity;
  const girth = 0.65 + 0.6 * ctx.maturity;
  const h = range(rng, sp.trunk.height[0], sp.trunk.height[1]) * grow;
  const r0 = sp.trunk.radius * girth;
  const winter = ctx.season === "winter";
  const bark: C3 = winter ? desat(sp.palette.bark, 0.3) : sp.palette.bark;
  const leaf: C3 = winter ? desat(leafColor(sp, ctx.season), 0.35) : leafColor(sp, ctx.season);
  {
    const topY = h * sp.trunk.forkHeight;
    parts.push(tube(V(0, 0, 0), V(0, topY, 0), r0, r0 * 0.6, bark, 4));
    if (sp.deciduous && winter) {
      // Bare far silhouette: two skinny twig strokes.
      parts.push(tube(V(0, topY, 0), V(range(rng, -0.8, 0.8), topY + h * 0.3, range(rng, -0.8, 0.8)), r0 * 0.4, r0 * 0.15, bark, 4));
      parts.push(tube(V(0, topY, 0), V(range(rng, -0.8, 0.8), topY + h * 0.26, range(rng, -0.8, 0.8)), r0 * 0.35, r0 * 0.12, bark, 4));
    } else {
      const rr = range(rng, sp.canopy.size[0], sp.canopy.size[1]) * (1.5 + 0.9 * ctx.maturity);
      const crown = blob(rng, V(0, 0, 0), rr, shade(leaf, range(rng, 0.85, 1.05)), sp.canopy.rough, 0, 1);
      crown.scale(1.4, 0.72, 1.4); // match the near build's wide-canopy squash
      crown.translate(0, topY + rr * 0.5, 0);
      parts.push(crown);
    }
  }

  for (const part of parts) paintTreeWind(part);
  const merged = mergeGeometries(parts, false);
  if (!merged) throw new Error(`buildTreeFar(${sp.id}): merge failed`);
  for (const p of parts) p.dispose();
  merged.computeVertexNormals();
  return merged;
}

/* ---- motif/season hooks ---- */

/** Birch: thin, slightly wider dark rings read as bark bands. */
function addBarkBands(parts: THREE.BufferGeometry[], rng: Rng, sp: SpeciesParams, r0: number, top: THREE.Vector3): void {
  if (!sp.motifs.barkBands) return;
  const bands = 2 + Math.floor(rng() * 3);
  for (let i = 0; i < bands; i++) {
    const f = range(rng, 0.15, 0.9); // fraction up the trunk
    const ring = flat(new THREE.CylinderGeometry(r0 * 1.07, r0 * 1.07, 0.09, 5, 1));
    ring.translate(top.x * f, top.y * f, top.z * f);
    parts.push(paint(ring, [0.16, 0.15, 0.14]));
  }
}

/** Willow: an umbrella dome per tip with elongated strand blobs hanging off its rim. */
function addFronds(parts: THREE.BufferGeometry[], rng: Rng, sp: SpeciesParams, tips: THREE.Vector3[], leaf: C3, joints: WindJoint[]): void {
  for (const [index, tip] of tips.entries()) {
    const start = parts.length;
    const domeR = range(rng, sp.canopy.size[0], sp.canopy.size[1]) * 1.35;
    const dome = blob(rng, V(0, 0, 0), domeR, shade(leaf, range(rng, 0.85, 1.05)), sp.canopy.rough, 0, 1);
    dome.scale(1.15, 0.7, 1.15);
    dome.translate(tip.x, tip.y + domeR * 0.25, tip.z);
    parts.push(dome);
    const fronds = Math.round(range(rng, sp.canopy.count[0], sp.canopy.count[1]));
    for (let i = 0; i < fronds; i++) {
      const a = rng() * Math.PI * 2;
      const ringR = domeR * range(rng, 0.55, 0.95);
      const g = blob(rng, V(0, 0, 0), range(rng, sp.canopy.size[0], sp.canopy.size[1]), shade(leaf, range(rng, 0.8, 1.05)), sp.canopy.rough, 0, 1);
      g.scale(0.38, 1.55, 0.38); // hanging strand silhouette
      g.translate(tip.x + Math.cos(a) * ringR, tip.y - range(rng, 0.5, 1.2), tip.z + Math.sin(a) * ringR);
      parts.push(g);
    }
    for (const part of parts.slice(start)) paintTreeWind(part, joints[index]);
  }
}
/** Winter deciduous: recursive bare twigs where the canopy was, plus snow lumps. */
function addWinterCrown(parts: THREE.BufferGeometry[], rng: Rng, tips: THREE.Vector3[], r0: number, bark: C3, joints: WindJoint[]): void {
  const twig = (a: THREE.Vector3, dir: THREE.Vector3, len: number, r: number, depth: number): void => {
    const b = a.clone().addScaledVector(dir, len);
    parts.push(tube(a, b, r, r * 0.55, bark, 5, 0.3));
    if (depth <= 0 || r < 0.04) return;
    for (let i = 0; i < 2; i++) {
      const nd = dir.clone().add(V(range(rng, -0.8, 0.8), range(rng, 0.15, 0.7), range(rng, -0.8, 0.8))).normalize();
      twig(b, nd, len * range(rng, 0.55, 0.75), r * 0.55, depth - 1);
    }
  };
  for (const [index, tip] of tips.entries()) {
    const start = parts.length;
    twig(tip, V(range(rng, -0.4, 0.4), 1, range(rng, -0.4, 0.4)).normalize(), range(rng, 0.5, 0.9), r0 * 0.26, 2);
    if (rng() < 0.8) parts.push(blob(rng, tip.clone().add(V(0, 0.15, 0)), range(rng, 0.16, 0.3), SNOW, 0.3, 0, 0.3));
    const twigTip = tip.clone().add(V(0, .8, 0));
    for (const part of parts.slice(start)) paintTreeWind(part, joints[index], windJoint(tip, twigTip, r0 * .26));
  }
}
